import { Box, Flex, IconButton, Text, Tooltip } from '@chakra-ui/react'
import React, { useEffect, useState } from 'react'
import {
  FiLogOut,
  FiMaximize,
  FiMinimize,
  FiRefreshCw,
  FiSettings,
  FiWifi,
  FiWifiOff,
  FiXCircle,
} from 'react-icons/fi'
import ConfigModal from './ConfigModal'
import VersionInfo from './VersionInfo'

type FooterProps = {
  operatorName: string;
  onLogout?: () => void;
  onLeaveRoom?: () => void;
  onSettings?: () => void;
  onToggleFullscreen?: () => void;
};

const Footer: React.FC<FooterProps> = ({
  operatorName,
  onLogout,
  onLeaveRoom,
  onSettings,
  onToggleFullscreen,
}) => {
  const [dateTime, setDateTime] = useState(new Date());
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [version, setVersion] = useState<string>('v1.0.10');

  // Atualiza a data/hora a cada segundo
  useEffect(() => {
    const interval = setInterval(() => setDateTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Monitora a conexão com a internet
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Alterna o modo de tela cheia
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
        .then(() => setIsFullscreen(true))
        .catch((err) => console.error(err));
    } else {
      document.exitFullscreen()
        .then(() => setIsFullscreen(false))
        .catch((err) => console.error(err));
    }
    onToggleFullscreen && onToggleFullscreen();
  };

  // Força a altura dos botões a 24px (usando !important)
  const btnSx = {
    height: '24px !important',
    minHeight: '24px !important',
    lineHeight: '24px !important',
    padding: '0px !important',
  };

  return (<>
    <ConfigModal isOpen={isConfigOpen} onClose={() => setIsConfigOpen(false)} />
    <Box
      as="footer"
      position="fixed"
      bottom="0"
      left="0"
      width="100%"
      h="40px"
      bg="gray.50"
      color="gray.800"
      px={3}
      py={0.5}
      borderTop="1px solid"
      borderColor="gray.200"
      zIndex={1000}
    >
      <Flex align="center" justify="space-between" h="100%">
        <Flex align="center" gap={1}>
          <Tooltip label="Deslogar" aria-label="Deslogar">
            <IconButton
              aria-label="Deslogar"
              icon={<FiLogOut size={14} />}
              size="xs"
              variant="ghost"
              onClick={onLogout}
              _hover={{ color: 'red.500' }}
              minW="36px"
              sx={btnSx}
            />
          </Tooltip>
          {/* <Tooltip label="Sair da Sala" aria-label="Sair da Sala">
            <IconButton
              aria-label="Sair da Sala"
              icon={<FiXCircle size={14} />}
              size="xs"
              variant="ghost"
              onClick={onLeaveRoom}
              _hover={{ color: 'orange.500' }}
              minW="36px"
              sx={btnSx}
            />
          </Tooltip> */}
          <Tooltip label="Configurações" aria-label="Configurações">
            <IconButton
              aria-label="Configurações"
              icon={<FiSettings size={14} />}
              size="xs"
              variant="ghost"
              onClick={() => setIsConfigOpen(true)}
              _hover={{ color: 'blue.500' }}
              minW="36px"
              sx={btnSx}
            />
          </Tooltip>
          <Tooltip label="Tela Cheia" aria-label="Tela Cheia">
            <IconButton
              aria-label="Tela Cheia"
              icon={isFullscreen ? <FiMinimize size={14} /> : <FiMaximize size={14} />}
              size="xs"
              variant="ghost"
              onClick={toggleFullscreen}
              _hover={{ color: 'green.500' }}
              minW="36px"
              sx={btnSx}
            />
          </Tooltip>
          <Tooltip label={isOnline ? 'Conectado' : 'Offline'} aria-label="Conexão">
            <IconButton
              aria-label="Conexão com a Internet"
              icon={isOnline ? <FiWifi size={14} /> : <FiWifiOff size={14} />}
              size="xs"
              variant="ghost"
              bg={isOnline ? 'green.500' : 'red.500'}
              _hover={{ bg: isOnline ? 'green.600' : 'red.600' }}
              minW="36px"
              sx={btnSx}
              color="white"
            />
          </Tooltip>
          <Tooltip label="Atualizar Página" aria-label="Atualizar Página">
            <IconButton
              aria-label="Atualizar Página"
              icon={<FiRefreshCw size={14} />}
              size="xs"
              variant="ghost"
              onClick={() => window.location.reload()} // 🔄 Refresh da tela
              _hover={{ color: 'teal.500' }}
              minW="36px"
              sx={btnSx}
            />
          </Tooltip>
        </Flex>
        <Flex align="center" gap={3}>
          <Text fontSize="xs" color="gray.500">Versão: </Text> <VersionInfo />
          |
          <Text fontSize="xs" fontWeight="semibold">
            {operatorName}
          </Text>
          |
          <Text fontSize="xs">
            {dateTime.toLocaleDateString()} {dateTime.toLocaleTimeString()}
          </Text>          
        </Flex>
      </Flex>
    </Box>
  </>);
};

export default Footer;
